import { useState, useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { supabase } from './supabaseClient';
import { useTenant } from './context/TenantContext';

const LoginPortal = ({ onLogin }) => {
  const [activeTab, setActiveTab] = useState('ops');
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [lastFour, setLastFour] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const usernameInputRef = useRef(null);
  const pinInputRef = useRef(null);
  const lastFourInputRef = useRef(null);
  const { tenant } = useTenant();

  useEffect(() => {
    if (activeTab === 'ops' && usernameInputRef.current) {
      usernameInputRef.current.focus();
    } else if (activeTab === 'client' && lastFourInputRef.current) {
      lastFourInputRef.current.focus();
    }
  }, [activeTab]);

  const handleOpsLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password: pin,
      });

      if (authError) throw authError;

      // Explicitly propagate session so RLS auth.uid() resolves correctly
      await supabase.auth.setSession({
        access_token: authData.session.access_token,
        refresh_token: authData.session.refresh_token,
      });

      console.log('[Login] auth success, fetching role...');

      const { data: staffData, error: staffError } = await supabase
        .from('staff_members')
        .select('role, username, tenant_id, is_super_admin')
        .eq('id', authData.user.id)
        .maybeSingle();

      console.log('[Login] staff result:', staffData, staffError);

      if (!staffData?.is_super_admin && staffData?.tenant_id !== tenant?.id) {
        await supabase.auth.signOut();
        setError('Acceso no autorizado para este portal.');
        return;
      }

      const role = staffData?.role || 'admin';
      const username = staffData?.username || authData.user.email;

      console.log('[Login] role resolved:', role);

      onLogin({
        ...authData.user,
        role,
        username,
        full_name: username,
        is_super_admin: staffData?.is_super_admin || false,
      });

    } catch (err) {
      console.error('[Login] error:', err);
      setError(err.message || 'Invalid credentials, please try again.');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  const handleClientLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (lastFour.length !== 4) {
      setError('Please enter exactly the last 4 digits of your phone number.');
      return;
    }
    const sanitizedInput = lastFour.replace(/\D/g, '');
    const { data: customers, error: lookupError } = await supabase
      .from('customers')
      .select('id, full_name, phone_number')
      .eq('tenant_id', tenant?.id)
      .ilike('phone_number', '%' + sanitizedInput);
    console.log('[ClientLogin] phone lookup result:', customers);
    console.log('[ClientLogin] phone lookup error:', lookupError);
    if (!customers || customers.length === 0) {
      setError('No account found with those phone digits.');
      setLastFour('');
      return;
    }
    if (customers.length > 1) {
      setError('Multiple accounts found. Please contact the store.');
      setLastFour('');
      return;
    }
    onLogin({ ...customers[0], role: 'client' });
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div className="tile" style={{ width: '100%', maxWidth: '380px', margin: '0 auto' }}>
        <div className="text-center mb-6">
          {/* Fixed optical height so logos of any aspect ratio render at the same
              size and stay centered. objectFit: contain never crops. A missing or
              broken tenant logo_url falls back to the SivarSend mark. */}
          <img
            src={tenant?.logo_url || '/icon-512.png'}
            alt={tenant?.company_name || 'SivarSend'}
            style={{
              display: 'block',
              margin: '0 auto 16px',
              height: '80px',
              width: 'auto',
              maxWidth: '160px',
              objectFit: 'contain',
              borderRadius: '12px'
            }}
            onError={(e) => { e.target.src = '/icon-512.png'; }}
          />
          <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>
            powered by <span style={{ fontWeight: '700' }}>Sivar</span><span style={{ fontWeight: '700', color: 'var(--accent, #e53e3e)' }}>Send</span>
          </p>
          <p className="page-subtitle">Sistema de Gestión de Manifiestos</p>
        </div>

        <div className="nav-tabs mb-6" style={{ background: 'var(--bg)', padding: '4px', borderRadius: 'var(--radius-lg)' }}>
          <button 
            className={`nav-tab ${activeTab === 'ops' ? 'active' : ''}`}
            onClick={() => setActiveTab('ops')}
            style={{ flex: 1, justifyContent: 'center' }}
          >
            Operativo
          </button>
          <button 
            className={`nav-tab ${activeTab === 'client' ? 'active' : ''}`}
            onClick={() => setActiveTab('client')}
            style={{ flex: 1, justifyContent: 'center' }}
          >
            Cliente
          </button>
        </div>

        {activeTab === 'ops' ? (
          <form onSubmit={handleOpsLogin} className="space-y-4">
            <div className="space-y-1">
              <label className="metric-label">Usuario</label>
              <input
                type="text"
                className="input"
                placeholder="nombre@ejemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                ref={usernameInputRef}
                required
              />
            </div>
            <div className="space-y-1">
              <label className="metric-label">Contraseña</label>
              <input
                type="password"
                className="input"
                placeholder="••••••••"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                ref={pinInputRef}
                required
              />
            </div>
            {error && (
              <p className="badge badge-error w-full text-center" style={{ padding: '8px' }}>{error}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full"
              style={{ justifyContent: 'center', height: '42px', fontSize: '14px' }}
            >
              {loading ? 'Autenticando...' : 'Entrar al Sistema'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleClientLogin} className="space-y-4">
            <p className="text-sm text-gray-500 text-center mb-4" style={{ color: 'var(--text-secondary)' }}>
              Ingrese los últimos 4 dígitos de su teléfono para rastrear sus paquetes.
            </p>
            <input
              type="text"
              maxLength="4"
              className="input"
              style={{ fontSize: '32px', textAlign: 'center', height: '64px', fontWeight: '700', letterSpacing: '8px' }}
              placeholder="0000"
              value={lastFour}
              onChange={(e) => setLastFour(e.target.value)}
              ref={lastFourInputRef}
            />
            {error && (
              <p className="badge badge-error w-full text-center" style={{ padding: '8px' }}>{error}</p>
            )}
            <button
              type="submit"
              className="btn btn-primary w-full"
              style={{ justifyContent: 'center', height: '42px', fontSize: '14px' }}
            >
              Rastrear Paquetes <ArrowRight size={18} className="ml-2" />
            </button>
          </form>
        )}

        <div className="divider" style={{ margin: '24px 0 12px' }} />
        <p className="text-center" style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>
          SivarSend · para los encomendistas
        </p>
      </div>
    </div>
  );
};

export default LoginPortal;
